import { Suspense, lazy, useEffect, useRef } from "react";
import { Route, Switch } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { queryClient } from "@/lib/queryClient";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { useIdleTimer } from "@/hooks/use-idle-timer";
import { useLockPrefs } from "@/hooks/use-lock-prefs";
import { useSession } from "@/hooks/use-session";
import { DesktopLayout } from "@/components/DesktopLayout";
import { AuthGuard } from "@/components/AuthGuard";
import { AdminGuard } from "@/components/AdminGuard";
import { LockOverlay } from "@/components/LockOverlay";
import { MobileLockOverlay } from "@/mobile/components/MobileLockOverlay";
import { useIdentify } from "@/hooks/use-identify";
import { useLocked, wireLockEvents, setLocked as setLockStore } from "@/lib/lockStatus";
import { startSyncRunner } from "@/lib/offline/syncRunner";
import { useQueuedExpenses } from "@/lib/offline/useQueuedExpenses";

const MobileApp = lazy(() =>
  import("@/mobile/MobileApp").then((m) => ({ default: m.MobileApp })),
);
const Login = lazy(() => import("@/pages/Login"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const UnifiedHome = lazy(() => import("@/pages/UnifiedHome"));
const Settings = lazy(() => import("@/pages/Settings"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const Users = lazy(() => import("@/pages/Users"));
const InviteUser = lazy(() => import("@/pages/InviteUser"));
const TemplateAdmin = lazy(() => import("@/pages/TemplateAdmin"));
const DocumentTaxonomyAdmin = lazy(() => import("@/pages/DocumentTaxonomyAdmin"));
const PromissoryNotes = lazy(() => import("@/pages/PromissoryNotes"));
const Properties = lazy(() => import("@/pages/Properties"));
const Reserves = lazy(() => import("@/pages/Reserves"));
const Distributions = lazy(() => import("@/pages/Distributions"));
const DistributionDetail = lazy(() => import("@/pages/DistributionDetail"));
const Reports = lazy(() => import("@/pages/Reports"));
const PropertyPlReport = lazy(() => import("@/pages/PropertyPlReport"));
const TaxScheduleE = lazy(() => import("@/pages/TaxScheduleE"));
const K1Report = lazy(() => import("@/pages/K1Report"));
const EntitySummary = lazy(() => import("@/pages/EntitySummary"));
const SunkCostPool = lazy(() => import("@/pages/SunkCostPool"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const NotificationsPage = lazy(() => import("@/pages/Notifications"));
const PropertyCreate = lazy(() => import("@/pages/PropertyCreate"));
const PropertyDetail = lazy(() => import("@/pages/PropertyDetail"));
const DrawRequests = lazy(() => import("@/pages/DrawRequests"));
const NotFound = lazy(() => import("@/pages/not-found"));

const loader = (
  <div className="flex min-h-screen items-center justify-center">
    <p className="text-muted-foreground">Loading…</p>
  </div>
);

function DesktopApp() {
  return (
    <Suspense fallback={loader}>
      <Switch>
        <Route path="/login">
          <Login />
        </Route>
        <Route path="/accept-invite">
          <AcceptInvite />
        </Route>
        <Route path="/forgot-password">
          <ForgotPassword />
        </Route>
        <Route path="/reset-password">
          <ResetPassword />
        </Route>
        <Route path="/">
          <AuthGuard>
            <DesktopLayout>
              <UnifiedHome />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/settings">
          <AuthGuard>
            <DesktopLayout>
              <Settings />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/settings/audit-log">
          <AuthGuard>
            <AdminGuard>
              <DesktopLayout>
                <AuditLog />
              </DesktopLayout>
            </AdminGuard>
          </AuthGuard>
        </Route>
        <Route path="/users">
          <AuthGuard>
            <AdminGuard>
              <DesktopLayout>
                <Users />
              </DesktopLayout>
            </AdminGuard>
          </AuthGuard>
        </Route>
        <Route path="/users/invite">
          <AuthGuard>
            <AdminGuard>
              <DesktopLayout>
                <InviteUser />
              </DesktopLayout>
            </AdminGuard>
          </AuthGuard>
        </Route>
        <Route path="/admin/templates">
          <AuthGuard>
            <AdminGuard>
              <DesktopLayout>
                <TemplateAdmin />
              </DesktopLayout>
            </AdminGuard>
          </AuthGuard>
        </Route>
        <Route path="/admin/document-types">
          <AuthGuard>
            <AdminGuard>
              <DesktopLayout>
                <DocumentTaxonomyAdmin />
              </DesktopLayout>
            </AdminGuard>
          </AuthGuard>
        </Route>
        <Route path="/admin/promissory-notes">
          <AuthGuard>
            <AdminGuard>
              <DesktopLayout>
                <PromissoryNotes />
              </DesktopLayout>
            </AdminGuard>
          </AuthGuard>
        </Route>
        <Route path="/properties">
          <AuthGuard>
            <DesktopLayout>
              <Properties />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/reserves">
          <AuthGuard>
            <DesktopLayout>
              <Reserves />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/distributions">
          <AuthGuard>
            <DesktopLayout>
              <Distributions />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/distributions/:id">
          <AuthGuard>
            <DesktopLayout>
              <DistributionDetail />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/reports">
          <AuthGuard>
            <DesktopLayout>
              <Reports />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/reports/property-pl">
          <AuthGuard>
            <DesktopLayout>
              <PropertyPlReport />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/reports/tax-schedule-e">
          <AuthGuard>
            <DesktopLayout>
              <TaxScheduleE />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/reports/k1">
          <AuthGuard>
            <DesktopLayout>
              <K1Report />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/reports/entity-summary">
          <AuthGuard>
            <DesktopLayout>
              <EntitySummary />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/sunk-cost-pool">
          <AuthGuard>
            <DesktopLayout>
              <SunkCostPool />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/tasks">
          <AuthGuard>
            <DesktopLayout>
              <Tasks />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/notifications">
          <AuthGuard>
            <DesktopLayout>
              <NotificationsPage />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/properties/new">
          <AuthGuard>
            <DesktopLayout>
              <PropertyCreate />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/properties/:id">
          <AuthGuard>
            <DesktopLayout>
              <PropertyDetail />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/draw-requests">
          <AuthGuard>
            <DesktopLayout>
              <DrawRequests />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route>
          <AuthGuard>
            <DesktopLayout>
              <NotFound />
            </DesktopLayout>
          </AuthGuard>
        </Route>
      </Switch>
    </Suspense>
  );
}

function IdentifyEffect() {
  useIdentify();
  return null;
}

// Boots the offline expense queue's sync runner once for the whole
// app (idempotent via a module-level guard) and warns before the tab
// closes while a receipt blob is still queued for upload — closing
// then would lose the receipt because the BG-sync path can't drain
// blobs from a closed tab. Same shape on mobile and desktop now that
// desktop captures expenses with offline support too.
function OfflineSyncEffect() {
  useEffect(() => {
    startSyncRunner();
  }, []);
  const { queued } = useQueuedExpenses();
  const hasBlockingReceipt = queued.some(
    (q) =>
      (q.state === "pending" || q.state === "uploading_receipt") &&
      q.receipt?.kind === "queued",
  );
  useEffect(() => {
    if (!hasBlockingReceipt) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasBlockingReceipt]);
  return null;
}

// Wire the lock-state event listeners once, as a side effect of
// rendering the App. Idempotent via a module-level guard.
wireLockEvents();

// When the server tells us the session is gone (any 401 without a
// LOCKED code), wipe the cached /api/me so AuthGuard sees data=null
// and routes the user to /login. Has to live where the queryClient
// is in scope.
if (typeof window !== "undefined") {
  window.addEventListener("cerebellum:auth-expired", () => {
    queryClient.setQueryData(["/api/me"], null);
  });
}

// Service-worker update detection. vite-plugin-pwa is configured with
// `skipWaiting: true` + `clientsClaim: true`, so when a new SW
// installs after a deploy it immediately takes control of any open
// tab. The browser fires `controllerchange` on the navigator's SW
// container at that moment — we use that as the cue to surface a
// non-blocking "new version available" toast. Without this nudge,
// users who keep the PWA open in the foreground sit on the previous
// JS bundle until they happen to navigate.
function ServiceWorkerUpdater() {
  const shownRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;
    const onChange = () => {
      if (shownRef.current) return;
      shownRef.current = true;
      // Lazy import sonner to avoid pulling toast into the very first
      // render path.
      import("sonner").then(({ toast }) => {
        toast("A new version of Cerebellum is available.", {
          duration: Infinity,
          // Pin to the top so the toast never lands in the same area
          // as a Vaul drawer (mobile expense capture sheet, etc.) —
          // the drawer's portal would otherwise intercept the tap on
          // Reload despite Sonner's high z-index.
          position: "top-center",
          action: {
            label: "Reload",
            onClick: () => window.location.reload(),
          },
        });
      });
    };
    navigator.serviceWorker.addEventListener("controllerchange", onChange);
    return () => navigator.serviceWorker.removeEventListener("controllerchange", onChange);
  }, []);
  return null;
}

/**
 * Session-gated lock controller — renders nothing when the user isn't
 * authenticated so the idle timer doesn't fire on /login etc. Inside here
 * useSession has the QueryClient context it needs.
 */
function SessionLockController({ isMobile }: { isMobile: boolean }) {
  const { data: user } = useSession();
  const authed = Boolean(user);
  const { prefs } = useLockPrefs();
  const { idle, reset } = useIdleTimer(
    prefs.timeoutMinutes * 60 * 1000,
    prefs.enabled,
  );
  const locked = useLocked();
  const prevAuthedRef = useRef(authed);

  // When authed flips false → true (fresh login), clear any idle
  // state that accumulated on the login screen. Otherwise the
  // idle=true signal carries over and the lock fires on first render
  // of the authenticated app.
  //
  // When authed flips true → false (server-side session expiry, sign
  // out, etc.), clear the lock state too. Otherwise a stale
  // `locked === true` keeps the overlay sitting on top of the login
  // screen after auto-logout.
  useEffect(() => {
    if (!prevAuthedRef.current && authed) {
      reset();
      setLockStore(false);
    } else if (prevAuthedRef.current && !authed) {
      setLockStore(false);
    }
    prevAuthedRef.current = authed;
  }, [authed, reset]);

  useEffect(() => {
    if (!authed) return;
    if (idle) {
      fetch("/api/session/lock", { method: "POST", credentials: "include" }).catch(() => {});
      setLockStore(true);
    }
  }, [idle, authed]);

  // Belt-and-suspenders: if we somehow end up locked while not
  // authenticated, hide the overlay. The login screen owns the UI.
  if (!locked || !authed) return null;
  const onUnlock = () => {
    setLockStore(false);
    reset();
    // Refire every active query so the page underneath the overlay
    // lands on real data — /api/me, the route's own queries, the
    // 60-second pollers, etc. Without this, anything that errored
    // during the locked window keeps showing its cached error
    // (e.g. PropertyDetail's "Property not found.") until staleTime
    // elapses or the user reloads.
    queryClient.invalidateQueries();
  };
  return isMobile ? (
    <MobileLockOverlay onUnlock={onUnlock} />
  ) : (
    <LockOverlay onUnlock={onUnlock} />
  );
}

export function App() {
  const isMobile = useIsMobile();
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <IdentifyEffect />
          <OfflineSyncEffect />
          <ServiceWorkerUpdater />
          {isMobile ? <MobileApp /> : <DesktopApp />}
          <SessionLockController isMobile={isMobile} />
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
